/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/scope-ag-grid-notes-decorator.ts
 * FILE_ID: QB-AGGRID-NOTES-DECORATOR-001
 * PURPOSE: Decorate the Description column's AG-Grid ColDef so rows
 *          with `internal_notes` or `client_visible_notes` surface
 *          the note text as a hover tooltip AND show a small 📝
 *          indicator prefixed onto the visible cell text.
 * WHY: Bravada estimators paste long descriptions from Excel that
 *      include critical context (e.g. "Subcon adds $194,918.40 for
 *      independent testing — see KPI Summary row 14"). Currently
 *      those notes live in the OverridesTab side drawer — invisible
 *      while scanning the grid. Surfacing the note inline at the
 *      row level closes the gap: estimators see at a glance which
 *      rows carry caveats without opening every row's drawer.
 * I/O:
 *   ┌────────────────────────┐    ┌──────────────────────┐    ┌───────────────────────┐
 *   │ AgColDef from builder  │ -> │ wrap valueFormatter  │ -> │ ColDef with tooltip + │
 *   │                        │    │ + add tooltip getter │    │ inline 📝 indicator    │
 *   └────────────────────────┘    └──────────────────────┘    └───────────────────────┘
 * RELATED:
 *   - QB-AGGRID-COLUMNS-001 (scope-ag-grid-columns.ts) — caller
 *   - QB-AGGRID-PAD-DECORATOR-001 (scope-ag-grid-pad-decorator.ts) —
 *     sibling pattern this mirrors. Both must compose cleanly when
 *     applied to the same column (order matters: pad placeholder
 *     wraps notes wrapping).
 *   - QB-AGGRID-STYLES-001 (lazy-aggrid-styles.tsx) — global AG-Grid
 *     props live there; this decorator only mutates the colDef.
 */

import type { QuoteLineItem } from '@/lib/quoteApi'
import type { AgColDef } from './scope-ag-grid-columns'

/** Marker character prefixed to the description when the row has notes. */
export const NOTES_PREFIX = '📝 '

/**
 * Pull the best available notes string off a line item row.
 *
 * Resolution order: internal_notes wins over client_visible_notes
 * because internal notes contain the operational caveats estimators
 * need at-a-glance (audit reconciliation, subcon backfill source,
 * margin warnings). Client-visible notes are usually shorter and
 * client-friendly — useful but secondary.
 *
 * Returns null when the row has neither set (or the row itself is
 * a pad row with no `id` — pad rows by definition have no notes).
 */
export function extractRowNotes(
  data: QuoteLineItem | null | undefined,
): string | null {
  if (!data) return null
  const internal = (data.internal_notes ?? '').trim()
  if (internal) return internal
  const visible = (data.client_visible_notes ?? '').trim()
  if (visible) return visible
  return null
}

/**
 * Mutates `colDef` in place to add notes tooltip + 📝 prefix when the
 * row has notes. Returns the same colDef for fluent chaining. No-op
 * when the column isn't the Description column.
 *
 * The prefix is non-destructive: the underlying `description` field
 * value is unchanged, only the visible string in the cell gets the
 * 📝 marker. AG-Grid's tooltip module renders the full note on
 * hover (>800ms default delay, configured globally).
 *
 * Composes safely with `decorateColDefWithPadPlaceholder`: when the
 * row is a pad (empty placeholder mode), notes are by definition
 * absent, so the pad's italic-grey behaviour wins. When the row is
 * real and has notes, the 📝 + tooltip fire.
 */
export function decorateColDefWithNotesTooltip(colDef: AgColDef): AgColDef {
  if (colDef.colId !== 'description') return colDef

  const innerFormatter = colDef.valueFormatter

  colDef.tooltipValueGetter = ({
    data,
  }: {
    data?: QuoteLineItem | null
  }): string | null => extractRowNotes(data)

  colDef.valueFormatter = ({
    value,
    data,
    ...rest
  }: {
    value: unknown
    data?: QuoteLineItem | null
    [k: string]: unknown
  }) => {
    // Run the inner formatter first (e.g. the pad-row placeholder
    // decorator). If it produced a non-empty rendered string and
    // the row has notes, we prefix the marker.
    const inner =
      typeof innerFormatter === 'function'
        ? innerFormatter({ value, data, ...rest } as never)
        : value == null
          ? ''
          : String(value)
    const innerStr = inner == null ? '' : String(inner)
    const notes = extractRowNotes(data)
    if (!notes) return innerStr
    // Pad-row placeholder strings already start with their own ghost
    // text. Don't double-stamp the marker on those; pad rows never
    // carry real notes anyway (extractRowNotes returns null) so this
    // branch is defensive.
    if (!innerStr) return NOTES_PREFIX
    if (innerStr.startsWith(NOTES_PREFIX)) return innerStr
    return `${NOTES_PREFIX}${innerStr}`
  }

  // Add a cellClassRule so the css can subtly style rows-with-notes
  // (faint left border etc.). The rule is keyed off the same
  // extractRowNotes function so the visual indicator can't drift
  // out of sync with the tooltip content.
  const existing = colDef.cellClassRules ?? {}
  colDef.cellClassRules = {
    ...existing,
    'qb-row-has-notes': ({ data }: { data?: QuoteLineItem | null }) =>
      extractRowNotes(data) !== null,
  }

  return colDef
}
